<template>
  <div class="w-full p-4 max-w-3xl">
    <!-- The band stays on screen, as on My Expenses, so New Request is
         always in reach. -mt-4 cancels the page's own p-4, which otherwise
         leaves 16px of slack before the band catches. -->
    <div class="sticky top-14 z-30 bg-gray-200 -mx-4 -mt-4 px-4 pt-3 pb-4">
      <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-2">
        <h2 class="text-lg font-semibold">
          {{ standingIn ? `${standInLabel}'s Requests` : 'My Requests' }}
        </h2>
        <div class="flex items-center gap-2">
          <!-- One control for all three: the page you read, the request you
               file and the answer you post all follow the same person. They
               could be separated, but in the workflow this exists for they
               are always the same person, and one selector means there is
               no state in which you are reading as one colleague and
               submitting as another without noticing.

               The list of people is the server's (STAND_IN_USERNAMES); this
               only renders what it was handed. -->
          <label v-if="standInOptions.can_act_on_behalf" class="flex items-center gap-1.5">
            <span class="text-xs text-gray-500 whitespace-nowrap">Submitting as this person</span>
            <select
              v-model="standIn"
              class="h-9 px-2 border rounded-md text-sm max-w-[12rem]"
              :class="standingIn
                ? 'border-amber-600 bg-amber-50 text-amber-900'
                : 'border-gray-400'"
              @change="onStandInChange"
            >
              <option value="">Nobody — this is me</option>
              <option v-for="person in standInOptions.people" :key="person.id" :value="person.id">
                {{ person.label }}
              </option>
            </select>
          </label>
          <button
            class="px-3 py-1.5 text-sm rounded-md bg-accent-primary text-white hover:bg-accent-secondary"
            @click="openNew"
          >
            + New Request
          </button>
        </div>
      </div>

      <!-- Loud on purpose. Everything written from this page while it is up
           carries somebody else's name, and the one failure mode that
           matters is forgetting that. -->
      <p
        v-if="standingIn"
        class="text-sm rounded-md border border-amber-600 bg-amber-50 text-amber-900 px-2 py-1.5 mb-2"
      >
        You are submitting as {{ standInLabel }}. What you file and post here
        will carry their name; a note on each one records that you did it.
      </p>

      <p class="text-sm text-gray-500">
        Ask for a feature, report a bug, or flag something that needs doing.
        You don't need to know which team owns it.
      </p>
    </div>

    <p v-if="justSubmitted" class="text-sm text-green-700 mt-2">
      Sent — it is in the list below.
    </p>
    <p v-if="submitWarning" class="text-sm text-red-600 mt-2">{{ submitWarning }}</p>

    <div class="mt-4">
      <p v-if="loading" class="text-gray-500">Loading…</p>
      <p v-else-if="loadError" class="text-red-600">{{ loadError }}</p>
      <p v-else-if="!requests.length" class="text-gray-500">
        <template v-if="standingIn">
          {{ standInLabel }} has no requests yet. Submit one as them with
          New Request above.
        </template>
        <template v-else>
          Nothing yet. Use New Request above and it shows up here with its status.
        </template>
      </p>

      <div
        v-for="item in requests"
        :key="item.id"
        class="border border-gray-300 rounded-md bg-white p-3 mb-2"
      >
        <div class="flex items-start justify-between gap-3">
          <p class="whitespace-pre-line text-sm">{{ item.body }}</p>
          <div class="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
            <span
              class="text-xs px-2 py-0.5 rounded"
              :class="requestStatusChip(item.status)"
            >{{ item.status_display }}</span>
            <!-- 'Over to you' is the whole point of the needs_info status: it
                 is no use telling somebody we are waiting unless they can see
                 it is their move. -->
            <span
              v-if="item.next_step_with === 'reporter'"
              class="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-800"
            >Over to you</span>
          </div>
        </div>

        <p v-if="item.public_summary" class="text-sm text-gray-700 mt-2">
          {{ item.public_summary }}
        </p>
        <p v-if="item.resolution_note" class="text-sm text-gray-700 mt-1">
          {{ item.resolution_note }}
        </p>
        <p class="text-xs text-gray-500 mt-1">
          Submitted {{ formatDateTimeWithDay(item.created_at) }}
          <span v-if="item.estimate_display"> · estimated {{ item.estimate_display }}</span>
          <span v-if="item.public_eta"> · expected {{ item.public_eta }}</span>
        </p>

        <!-- The thread reads the way two people talking reads in a
             message app: whoever asked on the left, whoever answered on
             the right, so you follow who said what without reading a
             name on every line. Indented under the request, because
             every post on it belongs to it. -->
        <div
          v-if="item.posts && item.posts.length"
          class="mt-3 ml-4 pl-3 border-l-2 border-gray-200 space-y-2"
        >
          <div
            v-for="post in item.posts"
            :key="post.id"
            class="flex"
            :class="fromRequestor(post) ? 'justify-end' : 'justify-start'"
          >
            <div class="max-w-[80%]">
              <div
                class="rounded-2xl px-3 py-2"
                :class="fromRequestor(post)
                  ? 'bg-accent-primary text-white rounded-br-sm'
                  : 'bg-gray-100 text-gray-900 border border-gray-300 rounded-bl-sm'"
              >
                <p class="whitespace-pre-line text-sm">{{ post.body }}</p>
              </div>
              <p
                class="text-xs text-gray-500 mt-1"
                :class="fromRequestor(post) ? 'text-right' : 'text-left'"
              >
                {{ post.author_display || 'Back office team' }}
                · {{ formatDateTimeWithDay(post.created_at) }}
              </p>
            </div>
          </div>
        </div>

        <!-- Your own request, so you may add to it or take a file back off. -->
        <attachment-strip
          :attachments="item.attachments || []"
          target-type="request"
          :target-id="item.id"
          :can-upload="!isClosed(item)"
          :can-remove="!isClosed(item)"
        />

        <div v-if="!isClosed(item)" class="mt-3">
          <textarea
            v-model="replies[item.id]"
            rows="2"
            class="w-full border border-gray-400 rounded-md px-2 py-1.5 text-sm"
            :placeholder="standingIn
              ? `Answer as ${standInLabel}`
              : (item.next_step_with === 'reporter'
                ? 'Answer here and it goes straight back to the team'
                : 'Add anything else that would help')"
          ></textarea>
          <div class="flex items-center gap-3 mt-1">
            <button
              class="px-3 py-1.5 rounded-md text-sm text-white bg-accent-primary
                     hover:bg-accent-secondary disabled:opacity-50"
              :disabled="!String(replies[item.id] || '').trim() || replying === item.id"
              @click="reply(item)"
            >
              {{ replying === item.id
                ? 'Sending…'
                : (standingIn ? `Reply as ${standInLabel}` : 'Reply') }}
            </button>
            <span v-if="replyErrors[item.id]" class="text-sm text-red-600">
              {{ replyErrors[item.id] }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <new-request-modal
      v-if="showNew"
      :on-behalf-of="standIn || null"
      :on-behalf-of-label="standInLabel"
      @close="showNew = false"
      @created="onCreated"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

import { formatDateTimeWithDay } from '@/composables/formatDateTime'

import AttachmentStrip from '@/components/back-office/AttachmentStrip.vue'
import NewRequestModal from '@/components/back-office/NewRequestModal.vue'
import { getMyRequests, getStandInOptions, replyToRequest } from '@/services/BackOffice'
import { REQUEST_CLOSED_STATUSES, requestStatusChip } from '@/utils/requestOptions'

const store = useStore()
const token = () => store.getters['auth/showToken']

const requests = ref([])
const loading = ref(true)
const showNew = ref(false)
const justSubmitted = ref(false)
const submitWarning = ref('')
const loadError = ref('')
const replies = ref({})
const replyErrors = ref({})
const replying = ref(null)

// --- submitting as a colleague -------------------------------------------
//
// Requests still arrive over Slack until this opens to the company. The team
// mirrors those conversations here, as the person who asked, so the tool is
// used the way an employee would use it before an employee is asked to use
// it.
//
// Deliberately NOT persisted. Every other choice on these screens survives
// a reload by design; an impersonation that quietly outlived the tab would
// put somebody else's name on the next thing typed here.
const standIn = ref('')
const standInOptions = ref({ can_act_on_behalf: false, people: [] })

const standingIn = computed(() => Boolean(standIn.value))
const standInLabel = computed(() => {
  const person = standInOptions.value.people.find(p => p.id === standIn.value)
  return person ? person.label : ''
})

// The page is somebody else's now, so what is on screen belongs to the
// person before the switch. Clear the half-typed replies with it rather
// than carry them across into another person's thread.
async function onStandInChange() {
  replies.value = {}
  replyErrors.value = {}
  justSubmitted.value = false
  submitWarning.value = ''
  await load()
}

function openNew() {
  justSubmitted.value = false
  submitWarning.value = ''
  showNew.value = true
}

// The modal files the request itself and closes either way, so a warning
// here means the request exists and something about its files did not.
async function onCreated(warning) {
  showNew.value = false
  justSubmitted.value = true
  submitWarning.value = warning || ''
  await load()
}

// Who counts as "the person who asked" on this page -- and so which posts
// render on the right: the colleague you are submitting as, or you. A request
// thread has two sides, the reporter and the back office team, and this page
// is always read from the reporter's side, including when you are standing in
// for a colleague, because then you are the one speaking as them.
//
// Compared by display name because a shared post carries nothing else --
// MyRequestSerializer deliberately withholds the author's id here, and adding
// one would hand a reporter a user id.
//
// An author we cannot place reads as the team, which is the safer default:
// the team's side is the plain one, so an unknown name is never dressed up
// as the requestor's own words.
const requestorName = computed(
  () => standInLabel.value || store.getters['auth/showUserFullName'] || '',
)

function fromRequestor(post) {
  const author = String(post.author_display || '').trim().toLowerCase()
  const owner = String(requestorName.value || '').trim().toLowerCase()
  return Boolean(author) && Boolean(owner) && author === owner
}

function isClosed(item) {
  return REQUEST_CLOSED_STATUSES.includes(item.status)
}

async function reply(item) {
  replying.value = item.id
  replyErrors.value = { ...replyErrors.value, [item.id]: '' }
  try {
    const result = await replyToRequest(
      token(), item.id, String(replies.value[item.id] || '').trim(),
      standIn.value || null,
    )
    // Replying to a 'Need More Information' request hands it back to the
    // team, so the status on the row changes too -- take the server's word
    // for both rather than guessing.
    Object.assign(item, result.request)
    item.posts = result.posts
    replies.value = { ...replies.value, [item.id]: '' }
  } catch (e) {
    replyErrors.value = { ...replyErrors.value, [item.id]: describe(e) }
  } finally {
    replying.value = null
  }
}

async function load() {
  loading.value = true
  loadError.value = ''
  try {
    requests.value = await getMyRequests(token(), standIn.value || null)
  } catch (e) {
    loadError.value = describe(e)
  } finally {
    loading.value = false
  }
}

function describe(e) {
  if (e?.response?.status === 403) {
    return 'Back Office requests are not open yet.'
  }
  if (e?.response?.status) {
    const detail = e.response.data ? JSON.stringify(e.response.data) : ''
    return `${e.response.status} ${e.response.statusText || ''} ${detail}`.trim()
  }
  return String(e)
}

onMounted(async () => {
  // Safe for anyone: a caller who may not stand in gets
  // {can_act_on_behalf: false, people: []} rather than a 403, so the page
  // renders the same either way and the control simply does not appear.
  try {
    standInOptions.value = await getStandInOptions(token())
  } catch (e) {
    // Non-fatal. Without it the control is absent, which is the correct
    // fallback: the page still does everything it did before.
  }
  await load()
})
</script>
